import React from 'react';
import { Helmet } from 'react-helmet-async';

function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | FoodLens AI</title>
        <meta name="description" content="FoodLens AI Privacy Policy - Learn how we protect your data and respect your privacy. We collect minimal data and never sell your information." />
      </Helmet>

      <div className="bg-background min-h-screen">
        {/* Hero */}
        <section className="bg-gradient-hero py-16 md:py-24">
          <div className="section text-center">
            <h1 className="section-heading">Privacy Policy</h1>
            <p className="section-subheading mx-auto">
              Your privacy is our priority. Learn how we protect your data.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="section py-16">
          <div className="max-w-3xl mx-auto space-y-12">
            {/* Key Privacy Points */}
            <div className="bg-sage-100 rounded-2xl p-8">
              <h2 className="font-display text-2xl text-charcoal mb-4">Important Privacy Note</h2>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-sage-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  We do not collect any personal information from our users
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-sage-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  We do not track individual user behavior
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-sage-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  All scan data is completely anonymous
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-sage-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  We never share or sell any user data to third parties
                </li>
              </ul>
            </div>

            {/* Information Collection */}
            <div className="bg-surface rounded-2xl border border-sage-100 p-8">
              <h2 className="font-display text-2xl text-charcoal mb-4">Information Collection and Use</h2>
              <p className="text-gray-600 mb-4">FoodLens collects the following information:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-600 space-y-2">
                <li>Product barcodes you scan</li>
                <li>Scan history (stored locally on your device only)</li>
              </ul>
              <p className="text-gray-600">
                We use this information solely to provide you with product information regarding ingredient content.
                Optionally, if you choose to submit scans, this information is stored anonymously in our database
                to help improve scanning capabilities for everyone that uses the product.
              </p>
            </div>

            {/* Data Collection */}
            <div className="bg-surface rounded-2xl border border-sage-100 p-8">
              <h2 className="font-display text-2xl text-charcoal mb-4">Data Collection</h2>
              <p className="text-gray-600">
                We collect minimal data necessary to provide our service. Your privacy is our top priority,
                and we do not share your personal information with third parties.
              </p>
            </div>

            {/* Data Storage */}
            <div className="bg-surface rounded-2xl border border-sage-100 p-8">
              <h2 className="font-display text-2xl text-charcoal mb-4">Data Storage</h2>
              <p className="text-gray-600">
                Your scan history is stored locally on your device. You can clear this history at any time
                through the app settings.
              </p>
            </div>

            {/* Cookie Policy */}
            <div className="bg-surface rounded-2xl border border-sage-100 p-8">
              <h2 className="font-display text-2xl text-charcoal mb-6">Cookie Policy</h2>
              <p className="text-gray-600 mb-6">
                We use cookies and similar tracking technologies to track the activity on our service and hold certain information.
              </p>

              <h3 className="font-semibold text-charcoal mb-3">Types of Cookies We Use:</h3>
              <ul className="list-disc ml-6 mb-6 text-gray-600 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website.</li>
                <li><strong>Marketing Cookies:</strong> Enable us to deliver more relevant content and track marketing campaign effectiveness.</li>
              </ul>

              <h3 className="font-semibold text-charcoal mb-3">Your Cookie Choices:</h3>
              <p className="text-gray-600 mb-6">
                You can choose to accept or decline cookies. Most web browsers automatically accept cookies,
                but you can usually modify your browser settings to decline cookies if you prefer.
                You can also clear your cookie consent choice at any time by clearing your browser data.
              </p>

              <h3 className="font-semibold text-charcoal mb-3">Data Collection:</h3>
              <p className="text-gray-600 mb-3">When you accept cookies, we collect information about:</p>
              <ul className="list-disc ml-6 mb-6 text-gray-600 space-y-2">
                <li>Geographic location (city/country level)</li>
                <li>Device information</li>
                <li>Browser type and settings</li>
                <li>Website usage patterns</li>
                <li>Time and duration of visits</li>
              </ul>

              <h3 className="font-semibold text-charcoal mb-3">GDPR Compliance:</h3>
              <p className="text-gray-600 mb-3">Under GDPR, we process data only with explicit consent. You have the right to:</p>
              <ul className="list-disc ml-6 text-gray-600 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Withdraw consent at any time</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
            </div>

            {/* Changes */}
            <div className="bg-surface rounded-2xl border border-sage-100 p-8">
              <h2 className="font-display text-2xl text-charcoal mb-4">Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update our Privacy Policy from time to time. We will notify you of any changes
                by posting the new Privacy Policy on this page.
              </p>
            </div>

            {/* Contact */}
            <div className="text-center">
              <p className="text-gray-500 mb-4">Have questions about our privacy practices?</p>
              <a
                href="mailto:foodlens@rainierresearch.xyz"
                className="btn-primary"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Privacy;
